import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import ProductionPlansList from '../production/ProductionPlansList';
import PageLoader from '../common/PageLoader';
import PageHeader from '../common/PageHeader';
import './ProductionPlansPage.css';
import productionApi from '../../services/productionApi';
import ProductionKanbanBoard from "../production/ProductionKanbanBoard";

export default function ProductionPlansPage() {
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const { showToast } = useToast();
    const [viewMode, setViewMode] = useState(
        localStorage.getItem('productionViewMode') || 'list'
    );
    const [formats, setFormats] = useState([]);

    useEffect(() => {
        const fetchFormats = async () => {
            try {
                const data = await productionApi.getFormats();
                if (data && data.formats) {
                    setFormats(data.formats);
                }
            } catch (err) {
                console.error("Error loading formats:", err);
            }
        };
        fetchFormats();
    }, []);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey && e.key === 'l') setViewMode('list');
            if (e.ctrlKey && e.key === 'k') setViewMode('kanban');
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    const handleViewChange = (mode) => {
        setViewMode(mode);
        localStorage.setItem('productionViewMode', mode);
    };

    const loadPlans = useCallback(async (isSilent = false) => {
        try {
            if (!isSilent) {
                setIsLoading(true);
            } else {
                setIsRefreshing(true);
            }
            const plansResponse = await productionApi.listPlans();
            if (!plansResponse) {
                throw new Error('Failed to load production plans');
            }
            setPlans(plansResponse);
        } catch (error) {
            console.error('Error loading production data:', error);
            showToast('Failed to load production plans. Please try again.', 'error');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [showToast]);

    useEffect(() => {
        let cancelled = false;

        const wrappedLoad = async () => {
            try {
                await loadPlans(false);
            } catch (e) {
                if (!cancelled) {
                    console.error(e);
                }
            }
        };

        wrappedLoad();

        return () => {
            cancelled = true;
        };
    }, [loadPlans]);

    const handleRefresh = useCallback(async () => {
        await loadPlans(true);
    }, [loadPlans]);

    const handleDeletePlan = useCallback(async (planId, planName) => {
        const previousPlans = [...plans];
        setPlans(prevPlans => prevPlans.filter(plan => plan.id !== planId));

        try {
            await productionApi.removePlan(planId);
            showToast(`Production plan "${planName}" has been successfully deleted.`, 'success');
        } catch (error) {
            setPlans(previousPlans);
            console.error('Error deleting production plan:', error);

            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to delete production plan. Please try again.';
            showToast(errorMessage, 'error', 5000);
        }
    }, [plans, showToast]);

    const handleUpdatePlanStatus = useCallback(async (planId, newStatus) => {
        const existingPlan = plans.find(p => p.id === planId);
        if (!existingPlan) {
            return;
        }

        const payload = {
            name: existingPlan.name,
            planned_start_date: existingPlan.planned_start_date,
            planned_end_date: existingPlan.planned_end_date,
            status: newStatus,
            notes: existingPlan.notes,
            is_active: existingPlan.is_active ?? true
        };

        const previousPlans = [...plans];
        setPlans(prev => prev.map(p => (p.id === planId ? { ...p, status: newStatus } : p)));

        try {
            await productionApi.updatePlan(planId, payload);
            showToast(`Plan "${existingPlan.name}" status updated to ${newStatus}.`, 'success');
        } catch (error) {
            setPlans(previousPlans);
            console.error('Error updating plan status:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to update plan status.';
            showToast(msg, 'error', 5000);
        }
    }, [plans, showToast]);

    const handleUpdateRunStatus = useCallback(async (run, newStatus, options = {}) => {
        // const { completedBy } = options;

        const plan = plans.find(p => p.id === run.plan_id || p.id === run.planId);

        const normalizeStatusForBackend = () => {
            if (newStatus === 'completed') {
                return 'completed';
            }

            if (!newStatus || newStatus === 'planned') {
                return 'Planned';
            }

            const stage = newStatus.toLowerCase();
            const stageMap = {
                roast: 'Roast',
                flavor: 'Flavor',
                grind: 'Grind',
                brew: 'Brew',
                mix: 'Mix',
                keg: 'Keg',
                can: 'Can'
            };

            return stageMap[stage] || newStatus;
        };

        try {
            if (newStatus === 'completed') {
                const actualQty = options.actualQuantity != null && options.actualQuantity !== ''
                    ? Number(options.actualQuantity)
                    : (run.planned_quantity ?? run.quantity ?? 0);

                const storedUser = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
                let completedBy = '';
                if (storedUser) {
                    try {
                        const parsed = JSON.parse(storedUser);
                        completedBy = parsed.username || parsed.name || parsed.email || '';
                    } catch (e) {
                        completedBy = storedUser;
                    }
                }

                const payload = {
                    actual_quantity: actualQty,
                    produced_quantity: actualQty,
                    actual_yield: actualQty,
                    quantity: actualQty,
                    loss_reason: options.lossReason || '',
                    notes: options.lossReason ? `Loss Reason: ${options.lossReason}. ${run.notes ?? ''}` : (run.notes ?? ''),
                    completed_by: completedBy || 'system'
                };

                await productionApi.completeRun(run.id, payload);
                showToast(`Run marked as completed (Yield: ${actualQty}).`, 'success');
            } else {
                const payload = {
                    name: run.name ?? run.flavor,
                    planned_start_date: run.planned_start_date ?? plan?.planned_start_date ?? run.scheduled_start_time,
                    planned_end_date: run.planned_end_date ?? plan?.planned_end_date ?? run.scheduled_end_time,
                    status: normalizeStatusForBackend(),
                    notes: run.notes ?? '',
                    is_active: run.is_active ?? true
                };

                await productionApi.startRun(run.id, payload);
                showToast('Run status updated.', 'success');
            }

            await loadPlans(true);
        } catch (error) {
            console.error('Error updating run status:', error);
            const msg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to update run status.';
            showToast(msg, 'error', 5000);
            await loadPlans(true);
        }
    }, [plans, showToast, loadPlans]);

    const filteredPlans = plans.map(plan => {
        // Filter runs by search query matching name, product name, flavor, or notes
        const filteredRuns = (plan.production_runs || []).filter(run => {
            if (!searchTerm) return true;
            const query = searchTerm.toLowerCase();
            return (
                (run.product_name || '').toLowerCase().includes(query) ||
                (run.flavor || '').toLowerCase().includes(query) ||
                (run.name || '').toLowerCase().includes(query) ||
                (run.notes || '').toLowerCase().includes(query)
            );
        });

        // Plan matches if plan name/description matches
        const matchesPlan = searchTerm ? (
            (plan.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (plan.description || '').toLowerCase().includes(searchTerm.toLowerCase())
        ) : true;

        return {
            ...plan,
            // Keep all runs if plan name/desc matches, otherwise only matching runs
            production_runs: matchesPlan && !searchTerm ? (plan.production_runs || []) : filteredRuns,
            _matchesSearch: matchesPlan || filteredRuns.length > 0
        };
    }).filter(plan => {
        const matchesStatus = statusFilter === 'ALL' || plan.status === statusFilter;
        const matchesSearchVal = !searchTerm || plan._matchesSearch;
        return matchesStatus && matchesSearchVal;
    });

    const totalPlannedCans = filteredPlans.reduce((total, plan) => {
        const planRuns = plan.production_runs || [];
        return total + planRuns.reduce((runTotal, run) => runTotal + (run.planned_quantity || 0), 0);
    }, 0);

    if (isLoading) {
        return <PageLoader title="Loading production data..." message="Preparing your production board" />;
    }

    return (
        <div className="production-plans-page">
            <PageHeader
                title={
                    <span className="title-with-pill">
                        <span>Production Plans</span>
                        <span className="center-stat-pill">
                            <label>Total Planned Cans</label>
                            <span className="divider"></span>
                            <span className="stat-value">{totalPlannedCans.toLocaleString()}</span>
                        </span>
                    </span>
                }
                subtitle="Manage and monitor production schedules and runs"
                breadcrumbCurrent="Production Plans"
                actions={
                    <>
                        <div className="view-toggle">
                            <button
                                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => handleViewChange('list')}
                                title="List View"
                            >
                                <span className="icon-list">☰</span>
                                List
                            </button>
                            <button
                                className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                                onClick={() => handleViewChange('kanban')}
                                title="Kanban View"
                            >
                                <span className="icon-kanban">📋</span>
                                Kanban
                            </button>
                        </div>
                        <button onClick={handleRefresh} className="btn btn-secondary" disabled={isRefreshing}>
                            <span className={`icon-refresh ${isRefreshing ? 'spinning' : ''}`}></span>
                            {isRefreshing ? 'Syncing...' : 'Refresh'}
                        </button>
                    </>
                }
                filterRow={
                    <div className="production-controls">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search production plans..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="filter-controls">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="ALL">All Statuses</option>
                                <option value="planned">Planned</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>
                }
            />

            <div className="production-content">
                {viewMode === 'list' ? (
                    <ProductionPlansList
                        plans={filteredPlans}
                        onRefresh={handleRefresh}
                        onDelete={handleDeletePlan}
                        onUpdatePlanStatus={handleUpdatePlanStatus}
                        onUpdateRunStatus={handleUpdateRunStatus}
                        formats={formats}
                    />
                ) : (
                    <ProductionKanbanBoard
                        plans={filteredPlans}
                        onUpdateRunStatus={handleUpdateRunStatus}
                        onUpdatePlanStatus={handleUpdatePlanStatus}
                        formats={formats}
                        onRefresh={handleRefresh}
                    />
                )}
            </div>
        </div>
    );
}
